import React, { useEffect, useRef, useState } from 'react';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import KeyboardArrowDownRoundedIcon from '@material-ui/icons/KeyboardArrowDownRounded';
import { useLanguage, SUPPORTED_LANGS } from '../contexts/LanguageContext';
import { APP_LOGIN_URL } from '../constants';

const NAV_TEXTS = {
  en:      { about: 'About us', login: 'Login' },
  zh:      { about: '关于我们', login: '登录' },
  'zh-TW': { about: '關於我們', login: '登入' },
  fr:      { about: 'À propos', login: 'Connexion' },
  de:      { about: 'Über uns', login: 'Anmelden' },
  ja:      { about: '私たちについて', login: 'ログイン' },
  ko:      { about: '회사 소개', login: '로그인' },
  ar:      { about: 'من نحن', login: 'دخول' },
};

function Navbar() {
  const { language, setLanguage } = useLanguage();
  const t = NAV_TEXTS[language] || NAV_TEXTS.en;
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [activeLanguageIndex, setActiveLanguageIndex] = useState(0);
  const languageMenuRef = useRef(null);
  const languageTriggerRef = useRef(null);
  const selectedLanguage = SUPPORTED_LANGS.find(item => item.code === language) || SUPPORTED_LANGS[0];

  useEffect(() => {
    const selectedIndex = SUPPORTED_LANGS.findIndex(item => item.code === language);
    setActiveLanguageIndex(selectedIndex < 0 ? 0 : selectedIndex);
  }, [language]);

  useEffect(() => {
    if (!languageMenuOpen) return undefined;

    const handleOutsideClick = (event) => {
      if (!languageMenuRef.current?.contains(event.target)) {
        setLanguageMenuOpen(false);
      }
    };

    document.addEventListener('pointerdown', handleOutsideClick);
    return () => document.removeEventListener('pointerdown', handleOutsideClick);
  }, [languageMenuOpen]);

  const focusLanguageOption = (index) => {
    requestAnimationFrame(() => {
      languageMenuRef.current
        ?.querySelector(`[data-language-index="${index}"]`)
        ?.focus();
    });
  };

  const openLanguageMenu = () => {
    const selectedIndex = SUPPORTED_LANGS.findIndex(item => item.code === language);
    const nextIndex = selectedIndex < 0 ? 0 : selectedIndex;
    setActiveLanguageIndex(nextIndex);
    setLanguageMenuOpen(true);
    focusLanguageOption(nextIndex);
  };

  const closeLanguageMenu = (restoreFocus = false) => {
    setLanguageMenuOpen(false);
    if (restoreFocus) requestAnimationFrame(() => languageTriggerRef.current?.focus());
  };

  const selectLanguage = (code) => {
    setLanguage(code);
    closeLanguageMenu(true);
  };

  const handleLanguageKeyDown = (event) => {
    if (!languageMenuOpen) {
      if (['ArrowDown', 'ArrowUp', 'Enter', ' '].includes(event.key)) {
        event.preventDefault();
        openLanguageMenu();
      }
      return;
    }

    if (event.key === 'Escape') {
      event.preventDefault();
      closeLanguageMenu(true);
      return;
    }

    if (event.key === 'Tab') {
      closeLanguageMenu();
      return;
    }

    let nextIndex = activeLanguageIndex;
    if (event.key === 'ArrowDown') nextIndex = (activeLanguageIndex + 1) % SUPPORTED_LANGS.length;
    else if (event.key === 'ArrowUp') nextIndex = (activeLanguageIndex - 1 + SUPPORTED_LANGS.length) % SUPPORTED_LANGS.length;
    else if (event.key === 'Home') nextIndex = 0;
    else if (event.key === 'End') nextIndex = SUPPORTED_LANGS.length - 1;
    else if (['Enter', ' '].includes(event.key)) {
      event.preventDefault();
      selectLanguage(SUPPORTED_LANGS[activeLanguageIndex].code);
      return;
    } else return;

    event.preventDefault();
    setActiveLanguageIndex(nextIndex);
    focusLanguageOption(nextIndex);
  };

  return (
      <header className="ed-nav">
        <a className="ed-logo-lockup" href="/" aria-label="FleetGO AI Workbench home">
          <span className="ed-logo-mark" aria-hidden="true">
            <img
              className="ed-logo-mark-image"
              src="/images/brand/fleetgo-lockup-light.svg"
              alt=""
            />
          </span>
          <span className="ed-brand-copy">
            <span className="ed-brand-product">
              Fleet<span className="ed-brand-go">GO</span> AI Workbench
            </span>
          </span>
        </a>

        <nav className="ed-nav-links" aria-label="Primary navigation">
          <a href="#about">{t.about}</a>
        </nav>

        <div className="ed-actions">
          <div
            className="ed-lang-select"
            ref={languageMenuRef}
            onKeyDown={handleLanguageKeyDown}
          >
            <button
              className="ed-lang-trigger"
              type="button"
              ref={languageTriggerRef}
              aria-label="Choose language"
              aria-haspopup="listbox"
              aria-expanded={languageMenuOpen}
              onClick={() => languageMenuOpen ? closeLanguageMenu() : openLanguageMenu()}
            >
              <span className="ed-lang-trigger-label">{selectedLanguage.label}</span>
              <KeyboardArrowDownRoundedIcon className="ed-lang-caret" aria-hidden="true" />
            </button>

            {languageMenuOpen && (
              <div className="ed-lang-menu" role="listbox" aria-label="Language">
                {SUPPORTED_LANGS.map((item, index) => {
                  const isSelected = item.code === language;
                  return (
                    <button
                      className={`ed-lang-option${isSelected ? ' is-selected' : ''}`}
                      key={item.code}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      data-language-index={index}
                      tabIndex={index === activeLanguageIndex ? 0 : -1}
                      onFocus={() => setActiveLanguageIndex(index)}
                      onClick={() => selectLanguage(item.code)}
                    >
                      <span>{item.label}</span>
                      {isSelected && <CheckRoundedIcon className="ed-lang-check" aria-hidden="true" />}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <a
            className="ed-btn-login"
            href={APP_LOGIN_URL}
          >
            {t.login}
          </a>
        </div>
      </header>
  );
}

export default Navbar;
